import React, { useState, useMemo, useEffect, useCallback } from "react";
import {
  useTable,
  useRowSelect,
  useFilters,
  useSortBy,
  usePagination,
  useExpanded
} from "react-table";
import {
  FaMinusCircle,
  FaPlusCircle,
  FaSortAmountDown,
  FaSort,
  FaSortAmountUpAlt
} from "react-icons/fa";
import {
  showToast,
  formatDateWithDayjs,
  formatToUSD
} from "../../../../utils/utils";
import { COLUMNS } from "./DemoAccountColumns";
import { Link } from "react-router-dom";
import PropTypes from "prop-types";
import publicAxios from "../../../../utils/publicAxios";
import DualPagination from "../../../../components/common/DualPagination";
import DemoFundInvTable from "./fundinvtable/DemoFundInvTable";
import DemoAccSetupModal from "../../../modals/DemoAccSetupModal";
import CheckboxSelect from "../CheckboxSelect";
import "./fundtable.scss";
import useGA4EventAction from "../../../../config/GoogleAnalytics/useGA4EventAction";
import {
  auPhNumber,
  auPhNumberTag,
  buttonName,
  commonErrMsg,
  email,
  fundedInvoicesData,
  invalidToastContent,
  InvoiceDetails,
  nzPhNumber,
  nzPhNumberTag,
  nzText1,
  SearchText,
  successToastContent
} from "../../../../locales/en";
import {
  getDemoinvoiceUrl,
  getDemoQuoteSubmitURL
} from "../../../../utils/constants/apiEndPoints";
import {
  MAILTO_INFO_EMAIL,
  PAGE_INDEX,
  PAGE_SIZE_25
} from "../../../../utils/constants/appConstants";

const renderSortIcon = (column) => {
  if (!column.isSorted) {
    return <FaSort />;
  }
  return column.isSortedDesc ? <FaSortAmountDown /> : <FaSortAmountUpAlt />;
};

const DemoFundManualTable = ({ handleSendActive }) => {
  const columns = useMemo(() => COLUMNS, []);
  const [showButtons, setShowButtons] = useState(false);
  const [persistentSelectedRows, setPersistentSelectedRows] = useState([]);

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_25);
  const [expandedRows, setExpandedRows] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [tempSearchQuery, setTempSearchQuery] = useState("");
  const { submitDemoInvoiceGA4Event } = useGA4EventAction();
  const userData = localStorage.getItem("fundTap_demoUser_account_setup");
  const parsedData = JSON.parse(userData);
  const userId = parsedData?.id;

  const loadInvoceData = useCallback(
    async (pageNumber, pageSize) => {
      try {
        if (!userData || !userId) {
          return;
        }
        setLoading(true);
        const response = await publicAxios.get(getDemoinvoiceUrl(userId), {
          params: {
            query: searchQuery || undefined,
            page: pageNumber,
            size: pageSize,
            order: "asc"
          }
        });

        const responseData = response.data.data || [];
        setData(responseData);
        setTotalPages(response.data.pageInfo?.totalPages || 0);
        if (
          responseData.length === 0 &&
          response.data.errors?.some((err) => err.code === 404)
        ) {
          // for 404 error data
        }
      } catch (error) {
        if (error.response) {
          if (error.response.status === 404) {
            const responseData = error.response.data || {};
            const message = responseData.message || "";
            const errors = responseData.errors || [];
            if (
              message !== InvoiceDetails.fundtapInformation.errors.noInvoice &&
              !errors.includes(
                InvoiceDetails.fundtapInformation.errors.noInvoice
              )
            ) {
              showToast(errors.join(", "), "error");
              setTotalPages(1);
            }
            setData([]);
          } else if (error.response.status === 500) {
            showToast(error.response.data.errors, "error");
          } else {
            showToast(error.response.data.errors, "error");
          }
        }
      } finally {
        setLoading(false);
      }
    },
    [searchQuery, userId, userData]
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    state,
    page,
    gotoPage,
    previousPage,
    nextPage,
    canPreviousPage,
    canNextPage,
    pageCount,
    pageOptions
  } = useTable(
    {
      columns,
      data,
      manualPagination: true,
      pageCount: totalPages,
      autoResetPage: false,
      initialState: { pageIndex: PAGE_INDEX, pageSize: PAGE_SIZE_25 }
    },
    useFilters,
    useSortBy,
    useExpanded,
    usePagination,
    useRowSelect
  );

  const { pageIndex } = state;

  useEffect(() => {
    loadInvoceData(pageIndex, pageSize);
  }, [loadInvoceData, pageIndex, pageSize]);

  useEffect(() => {
    const selectedCount = persistentSelectedRows.length;

    setShowButtons(selectedCount > 0);
  }, [persistentSelectedRows.length]);

  const selectedTotalInvoiceAmount = (persistentSelectedRows) => {
    let total = 0;
    persistentSelectedRows?.forEach((row) => {
      total += parseFloat(row.outstandingAmount) || row.totalAmount;
    });
    return (total * 0.9).toFixed(2);
  };

  const toggleRow = (rowId) => {
    setExpandedRows((prevState) =>
      prevState.includes(rowId)
        ? prevState.filter((id) => id !== rowId)
        : [...prevState, rowId]
    );
  };

  const [demoAccModal, setDemoAccModal] = useState(false);

  // Handle changes in the search input
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setTempSearchQuery(value);
    if (value.trim() === "") {
      setSearchQuery("");
    }
  };

  // Handle key press events in the search input
  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      const trimmedQuery = tempSearchQuery.trim();
      setSearchQuery(trimmedQuery);
      gotoPage(0);
    }
  };

  const handleAcceptCode = async () => {
    try {
      const selectedInvoicesRequestBody = [];
      persistentSelectedRows.forEach((invoice) => {
        selectedInvoicesRequestBody.push({
          demoInvoiceId: invoice.id,
          demoInvoiceRepaymentDate: invoice.dueDate
        });
      });

      const response = await publicAxios.post(getDemoQuoteSubmitURL(userId), {
        demoInvoiceDetails: selectedInvoicesRequestBody
      });
      if (response.status === 200) {
        handleSendActive(true);
        setDemoAccModal(!demoAccModal);
        gotoPage(0);
        loadInvoceData(0, pageSize);
        setPersistentSelectedRows([]);
      }
      showToast(response.data.message, successToastContent.status);
      submitDemoInvoiceGA4Event(userId, selectedInvoicesRequestBody);
    } catch (error) {
      const errorMessage =
        error.response?.data?.errors ||
        error.response?.data?.message ||
        commonErrMsg.item4;
      showToast(errorMessage, invalidToastContent.status);
    }
  };

  const toggleDemoAccModal = () => {
    setDemoAccModal((prev) => !prev);
  };

  if (loading) {
    return (
      <div className="spinner-wrap bg-light loader_absolute">
        <div className="spinner-border custom-spinner-color" />
      </div>
    );
  }

  const currencyFields = ["totalAmount", "outstandingAmount"];
  const dateFields = ["dueDate"];

  const formatValue = (accessor, value, row) => {
    if (dateFields.includes(accessor)) {
      return value ? formatDateWithDayjs(value, "DD-MMM-YYYY") : "";
    }

    if (currencyFields.includes(accessor)) {
      if (accessor === "outstandingAmount") {
        return formatToUSD(value ?? row.totalAmount);
      }
      return formatToUSD(value);
    }

    if (accessor === "contactName") {
      return value || "";
    }

    return value;
  };

  return (
    <>
      {showButtons && (
        <div className="upper-quote-wrap mb-5">
          <div className="quote-heading">
            <h4>{fundedInvoicesData.title}</h4>
          </div>
          <div className="quote-body">
            <div className="quote-summary-container">
              <p>
                {fundedInvoicesData.toFund.content1}
                <span className="amount">
                  {` ${selectedTotalInvoiceAmount(persistentSelectedRows)}`}
                </span>
                {fundedInvoicesData.toFund.content2}
              </p>
              <p>
                <small>({fundedInvoicesData.toFund.content3})</small>
              </p>
            </div>
          </div>
          <div className="quote-footer text-center">
            <div className="accept-btn-wrap mb-3">
              <button
                className="btn btn-primary accept-btn"
                onClick={handleAcceptCode}
              >
                {buttonName.demoInvoice}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className={showButtons ? "hidden" : "visible"}>
        <div className="instraction-text">
          <span>
            <strong>{`${fundedInvoicesData.toFund.content6} `}</strong>
            <span>{fundedInvoicesData?.toFund?.content7}</span>
          </span>
        </div>
      </div>

      <table
        className="mt-2 mb-3 fundtable funded-tabledemo-account"
        {...getTableProps()}
      >
        <thead>
          {headerGroups.map((headerGroup) => {
            const { key, ...rest } = headerGroup.getHeaderGroupProps();
            return (
              <tr className="parentrow" {...rest} key={key}>
                <th />
                <th />
                {headerGroup.headers.map((column, index) => (
                  <th
                    key={column.id}
                    {...column.getHeaderProps(column.getSortByToggleProps())}
                  >
                    <span>{column.render("Header")}</span>
                    {index !== 0 && (
                      <span className="short-icon">
                        {renderSortIcon(column)}
                      </span>
                    )}
                  </th>
                ))}
              </tr>
            );
          })}
        </thead>
        <tbody {...getTableBodyProps()}>
          {page.length === 0 ? (
            <tr>
              <td colSpan={columns.length + 2} className="text-center">
                {fundedInvoicesData.statusMessage.msg13}
              </td>
            </tr>
          ) : (
            page.map((row) => {
              prepareRow(row);
              const isExpanded = expandedRows.includes(row.id);
              return (
                <React.Fragment key={row.id}>
                  <tr className="parentrow" {...row.getRowProps()}>
                    <td className="expand-td">
                      <span
                        className="expandbtn cursor-pointer"
                        onClick={() => toggleRow(row.id)}
                      >
                        {isExpanded ? <FaMinusCircle /> : <FaPlusCircle />}
                      </span>
                    </td>
                    <td>
                      <CheckboxSelect
                        invoiceData={row.original}
                        selectedRows={persistentSelectedRows}
                        setSelectedRows={setPersistentSelectedRows}
                      />
                    </td>
                    {row.cells.map((cell) => (
                      <td
                        key={cell.getCellProps().key}
                        {...cell.getCellProps()}
                      >
                        {cell.render("Cell")}
                      </td>
                    ))}
                  </tr>
                  {isExpanded && (
                    <tr className="childrow colexpand">
                      <td colSpan={columns.length + 2}>
                        <div className="tdbody">
                          {columns.map((column) => {
                            const value = row.original[column.accessor];

                            return (
                              <span
                                key={column.accessor}
                                data-label={column.Header}
                              >
                                {formatValue(
                                  column.accessor,
                                  value,
                                  row.original
                                )}
                              </span>
                            );
                          })}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })
          )}
        </tbody>
      </table>
      <div className="d-flex flex-wrap mt-5 mb-5 justify-content-sm-between search">
        <DualPagination
          gotoPage={gotoPage}
          previousPage={previousPage}
          nextPage={nextPage}
          canPreviousPage={canPreviousPage}
          canNextPage={canNextPage}
          pageIndex={pageIndex}
          pageCount={pageCount}
          pageOptions={pageOptions}
          setPageSize={setPageSize}
          pageSize={pageSize}
          dataLength={data.length}
        />

        <span>
          <input
            type="search"
            value={tempSearchQuery}
            onChange={handleSearchChange}
            placeholder={SearchText}
            onKeyDown={handleKeyDown}
            className="rounded-0"
          />
        </span>
      </div>
      {showButtons && persistentSelectedRows.length > 0 && (
        <div className="upper-quote-wrap mb-5">
          <div className="quote-table-wrap">
            <DemoFundInvTable selectInvoices={persistentSelectedRows} />
          </div>

          <div className="quote-footer text-center mt-3">
            <div className="accept-btn-wrap mb-3">
              <button
                className="btn btn-primary accept-btn"
                onClick={handleAcceptCode}
              >
                {buttonName.demoInvoice}
              </button>
            </div>
          </div>
        </div>
      )}

      <DemoAccSetupModal isOpen={demoAccModal} toggle={toggleDemoAccModal} />

      <p>
        {fundedInvoicesData.toFund.content14}
        <Link to={MAILTO_INFO_EMAIL}>{email}</Link>
        {fundedInvoicesData.toFund.content15}
        <Link to={`tel:${auPhNumberTag}`}>
          <strong>{auPhNumber}</strong>
        </Link>
        {nzText1}
        <Link to={`tel:${nzPhNumberTag}`}>
          <strong>{nzPhNumber}</strong>
        </Link>
      </p>
    </>
  );
};

DemoFundManualTable.propTypes = {
  handleSendActive: PropTypes.func.isRequired
};

export default DemoFundManualTable;
